import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { RecommendationCard, RISK, MedicalDisclaimer } from "../components/clinical";
import { FollowupFields, downloadBlob } from "../components/FollowupFields";
import { FinalDecision, DetailsToggle } from "../components/FinalDecision";
import { PrescribingAlert } from "../components/PrescribingAlert";
import { TreatmentPlan } from "../components/TreatmentPlan";
import { ClearancePanel } from "../components/ClearancePanel";
import { SpecialistResponses } from "../components/SpecialistResponses";
import { CaseEmergencies } from "../components/emergency/CaseEmergencies";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ClipboardCheck, Download, CheckCircle2, Clock } from "lucide-react";

const TIMING_FIELDS = ["stent_days_ago", "mi_days_ago", "stroke_days_ago", "radiation_gy", "transplant_months_ago", "biologic_last_dose_days", "cardiac_surgery_days_ago", "pregnancy_trimester", "implant_count", "antiresorptive_years"];

function DecisionView({ c }) {
  const r = RISK[c.decision?.overall_status || "green"];
  return (
    <div className="mt-5" data-testid="results-decision">
      <p className="sr-only" data-testid="results-overall-status">{r.label}</p>
      <PrescribingAlert p={c.decision?.prescribing} />
      <TreatmentPlan plan={c.decision?.plan} />
      <FinalDecision fd={c.decision?.final_decision} procedureName={c.decision?.procedure?.name || c.procedure_name} />
      <CaseEmergencies recs={c.decision?.recommendations} />
      <DetailsToggle count={c.decision?.recommendations?.length || 0}>
        {c.decision?.recommendations?.map((rec) => <RecommendationCard key={rec.item_id} rec={rec} />)}
      </DetailsToggle>
      <MedicalDisclaimer testid="results-medical-disclaimer" />
    </div>
  );
}

const lastClearance = (c) => (c.clearance_history || []).filter((e) => !e.superseded).at(-1);

const replyPending = (c) => {
  const l = c.reply_link;
  return Boolean(l && !l.closed && new Date(l.expires_at) > new Date() && l.slots?.length > (c.specialist_responses?.length || 0));
};

export default function CaseResults() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [defs, setDefs] = useState([]);
  const [values, setValues] = useState({});
  const [units, setUnits] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/cases/${id}`).then(({ data }) => { setC(data); setUnits(data.lab_units || {}); }).catch(() => toast.error("Case not found"));
    api.get("/labs/definitions").then(({ data }) => setDefs(data.labs)).catch(() => {});
  }, [id]);

  const submit = async () => {
    const labs = {}, timing = {};
    Object.entries(values).forEach(([k, v]) => { if (v !== "") (TIMING_FIELDS.includes(k) ? timing : labs)[k] = v; });
    if (!Object.keys(labs).length && !Object.keys(timing).length) return toast.error("Enter at least one result");
    setSaving(true);
    try {
      const { data } = await api.put(`/cases/${id}/results`, { labs, lab_units: units, timing });
      setC(data);
      setValues({});
      toast.success(data.status === "complete" ? "Results saved — final recommendation ready" : data.awaiting_clearance ? "Saved — now awaiting specialist approval" : "Saved — some information is still pending");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save results");
    } finally {
      setSaving(false);
    }
  };

  if (!c) return <div data-testid="case-results-page"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const fus = c.decision?.followups || [];

  return (
    <div data-testid="case-results-page" className="max-w-3xl">
      <Link to="/app/pending" className="text-sm text-sky-700 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Pending cases</Link>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-2">{c.patient?.name || "Patient"} — Enter results</h1>
      <p className="text-sm text-slate-600 mt-1" data-testid="results-case-ids"><span className="font-mono font-semibold">{c.case_number}</span>{c.patient?.file_no && ` · File ${c.patient.file_no}`}{c.patient?.phone && <> · <a href={`tel:${c.patient.phone}`} className="text-sky-700" dir="ltr" data-testid="results-patient-phone">{c.patient.phone}</a></>}</p>
      <p className="text-slate-500 mt-1">{c.procedure_name} · {c.conditions_names?.concat(c.medications_names || []).join(", ")}</p>

      {fus.length > 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mt-5" data-testid="results-form">
          <h4 className="font-heading font-semibold text-amber-900">Results / information needed</h4>
          <p className="text-sm text-amber-800 mt-0.5">Enter each value exactly as on the lab report and confirm the unit — it is converted automatically.</p>
          <FollowupFields followups={fus} values={values} units={units} labDefs={defs}
            onValue={(k, v) => setValues((x) => ({ ...x, [k]: v }))} onUnit={(k, u) => setUnits((x) => ({ ...x, [k]: u }))} />
          <Button onClick={submit} disabled={saving} data-testid="results-submit-btn" className="mt-4 bg-amber-600 hover:bg-amber-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ClipboardCheck className="w-4 h-4 mr-2" /> Save results & get recommendation</>}
          </Button>
        </div>
      ) : c.awaiting_clearance ? (
        <><SpecialistResponses c={c} /><ClearancePanel c={c} onUpdated={setC} /></>
      ) : replyPending(c) ? (
        <div data-testid="results-awaiting-replies" className="mt-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <Clock className="w-5 h-5" /> Waiting for specialist replies — {c.specialist_responses?.length || 0} of {c.reply_link.slots.length} received. The decision updates automatically as each reply arrives.
        </div>
      ) : (
        <div data-testid="results-complete" className="mt-5 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          <CheckCircle2 className="w-5 h-5" /> {c.specialist_responses?.length > 1
            ? `${c.specialist_responses.length} specialist replies — ${c.specialist_responses.filter((r) => r.outcome === "declined").length ? `${c.specialist_responses.filter((r) => r.outcome === "declined").length} not approved → treatment deferred` : "all approved"} — case complete.`
            : lastClearance(c) ? `Specialist response recorded (${lastClearance(c).outcome === "approved" ? "approved" : "not approved"}${lastClearance(c).specialist ? ` — ${lastClearance(c).specialist}` : ""}) — case complete.` : "All information received — this case is complete."}
          <Button size="sm" variant="outline" className="ml-auto" data-testid="results-referral-pdf"
            onClick={() => downloadBlob(api, `/cases/${id}/pdf`, `referral_${id}.pdf`).catch(() => toast.error("Could not generate PDF"))}>
            <Download className="w-4 h-4 mr-1.5" /> Consultation report PDF
          </Button>
        </div>
      )}
      {!c.awaiting_clearance && !fus.length && <SpecialistResponses c={c} />}
      <DecisionView c={c} />
    </div>
  );
}
